/**
 * World-space markers projected through ctx.camera: objectives, teammates,
 * pings. Off-screen markers clamp to a safe-area rectangle with an arrow that
 * points back at the target, exactly like an objective marker in COD.
 *
 * A single full-screen canvas; only ever a handful of draw calls.
 */
import * as THREE from 'three';
import { COL, rgba, clamp, fitCanvas, devicePx, smoothstep } from './Theme.js';
import { drawGlyphs, label } from './Glyphs.js';

const _v = new THREE.Vector3();
const _c = new THREE.Vector3();

export class Markers {
  constructor(root) {
    this.cv = document.createElement('canvas');
    this.cv.className = 'hud-c hud-markers';
    root.appendChild(this.cv);
    this.w = 1; this.h = 1;
    this.g = fitCanvas(this.cv, 1, 1);
  }

  resize(w, h, dpr = devicePx()) {
    this.w = w; this.h = h;
    this.g = fitCanvas(this.cv, w, h, dpr);
    this._bbox = null;
  }

  /** Record a per-marker dirty rect for the next frame's clear. */
  _mark(x, y, rx, ry) {
    this._next.push(Math.floor(x - rx), Math.floor(y - ry), Math.ceil(rx * 2), Math.ceil(ry * 2));
  }

  draw(list, camera, ads) {
    const g = this.g;
    // Clearing 1920x1080 every frame is the single most expensive thing this
    // overlay could do, so only the region touched last frame is cleared.
    const prev = this._bbox;
    if (prev) for (let i = 0; i < prev.length; i += 4) g.clearRect(prev[i], prev[i + 1], prev[i + 2], prev[i + 3]);
    this._next = [];
    this._bbox = null;
    if (!list.length || !camera) return;

    const W = this.w, H = this.h;
    const insetX = Math.max(52, W * 0.05);
    const insetY = Math.max(52, H * 0.09);
    const cx = W / 2, cy = H / 2;

    camera.updateMatrixWorld();
    const fade = 1 - smoothstep(0.55, 0.95, ads) * 0.55;

    for (const m of list) {
      _v.copy(m.position);
      _c.copy(_v).applyMatrix4(camera.matrixWorldInverse);
      const dist = _c.length();
      const behind = _c.z > 0;
      _v.project(camera);
      let x = (_v.x * 0.5 + 0.5) * W;
      let y = (-_v.y * 0.5 + 0.5) * H;
      if (behind) { x = W - x; y = H - y; }

      let offX = x - cx, offY = y - cy;
      let clamped = false;
      if (behind || x < insetX || x > W - insetX || y < insetY || y > H - insetY) {
        clamped = true;
        // scale the offset so it lands on the inset rectangle
        const sx = Math.abs(offX) > 1e-4 ? (W / 2 - insetX) / Math.abs(offX) : Infinity;
        const sy = Math.abs(offY) > 1e-4 ? (H / 2 - insetY) / Math.abs(offY) : Infinity;
        const s = Math.min(sx, sy);
        if (Number.isFinite(s)) { offX *= s; offY *= s; }
        else { offX = 0; offY = -(H / 2 - insetY); }
        x = cx + offX; y = cy + offY;
      }

      const col = m.color || (m.kind === 'ally' ? COL.ally : m.kind === 'enemy' ? COL.enemy : COL.amber);
      // Fade out anything sitting on the reticle — a name tag must never be the
      // thing you are trying to shoot through.
      const near = Math.hypot(x - cx, y - cy);
      const centreFade = clamp(smoothstep(34, 130, near), 0.12, 1);
      const a = (m.alpha ?? 1) * fade * centreFade * (m.kind === 'ally' ? 0.82 : 1);
      if (a <= 0.02) continue;
      this._mark(x, y, 62, 48);
      g.save();
      g.globalAlpha = a;

      if (clamped) {
        const ang = Math.atan2(offY, offX);
        g.save();
        g.translate(x, y);
        g.rotate(ang + Math.PI / 2);
        g.fillStyle = rgba('#000000', 0.7);
        arrow(g, 13);
        g.fillStyle = col;
        arrow(g, 10);
        g.restore();
        // distance under the arrow
        drawGlyphs(g, String(Math.round(dist)), x, y + 15, 12, {
          align: 'center', color: rgba(col, 0.85), weight: 0.18, tracking: 0.1,
        });
      } else if (m.kind === 'ally') {
        // Chevron + name + distance, the standard friendly tag.
        g.fillStyle = rgba('#000000', 0.72);
        chev(g, x, y, 10);
        g.fillStyle = col;
        chev(g, x, y, 7.6);
        if (m.label) {
          label(g, m.label, x, y + 10, {
            size: 11, weight: 700, tracking: 0.16, align: 'center', color: rgba(COL.white, 0.88),
          });
        }
        drawGlyphs(g, String(Math.round(dist)), x, y + 26, 10, {
          align: 'center', color: rgba(col, 0.75), weight: 0.20, tracking: 0.12,
        });
      } else {
        // Objective: chamfered diamond plate with its letter and range.
        const s = 13;
        g.beginPath();
        g.moveTo(x, y - s); g.lineTo(x + s, y); g.lineTo(x, y + s); g.lineTo(x - s, y);
        g.closePath();
        g.fillStyle = rgba('#04070A', 0.78);
        g.fill();
        g.lineWidth = 2;
        g.strokeStyle = col;
        g.stroke();
        label(g, m.label || '', x, y - 7, {
          size: 15, weight: 700, tracking: 0, align: 'center', color: col, halo: null,
        });
        drawGlyphs(g, String(Math.round(dist)), x, y + s + 6, 12, {
          align: 'center', color: rgba(col, 0.85), weight: 0.18, tracking: 0.12,
        });
        // little tick legs
        g.strokeStyle = rgba(col, 0.55);
        g.lineWidth = 1;
        g.beginPath();
        g.moveTo(x - s - 5, y); g.lineTo(x - s - 11, y);
        g.moveTo(x + s + 5, y); g.lineTo(x + s + 11, y);
        g.stroke();
      }
      g.restore();
    }

    this._bbox = this._next.length ? this._next : null;
  }
}

function arrow(g, s) {
  g.beginPath();
  g.moveTo(0, -s); g.lineTo(s * 0.78, s * 0.68); g.lineTo(0, s * 0.24); g.lineTo(-s * 0.78, s * 0.68);
  g.closePath(); g.fill();
}
function chev(g, x, y, s) {
  g.beginPath();
  g.moveTo(x, y - s); g.lineTo(x + s * 0.9, y + s * 0.7); g.lineTo(x, y + s * 0.24); g.lineTo(x - s * 0.9, y + s * 0.7);
  g.closePath(); g.fill();
}
